import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface PlatformSetting {
  key: string;
  value: string;
  description: string | null;
}

const MODULE_KEYS = [
  'astrologer_module_enabled',
  'astrologer_max_clients_per_astrologer',
  'astrologer_max_analyses_per_month',
  'astrologer_doc_storage_bucket',
];

export function AdminAstrologerSettingsPage() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setSaving]     = useState(false);
  const [fetching, setFetching]  = useState(true);
  const [saved, setSaved]        = useState(false);

  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('key, value, description')
      .in('key', MODULE_KEYS)
      .then(({ data }) => {
        const m: Record<string, string> = {};
        for (const s of (data ?? []) as PlatformSetting[]) m[s.key] = s.value;
        setSettings(m);
        setFetching(false);
      });
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    const upserts = Object.entries(settings).map(([key, value]) => ({ key, value, updated_at: new Date().toISOString() }));
    await supabase.from('platform_settings').upsert(upserts, { onConflict: 'key' });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1.25rem' };

  if (fetching) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando configurações…</div>;

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)', margin: '0 0 0.5rem', fontFamily: 'var(--font-display)' }}>
        Configurações do Módulo Astrólogo
      </h1>
      <div style={{ ...cardStyle, marginBottom: '1.25rem', background: 'rgba(245,200,66,0.06)', borderColor: 'rgba(245,200,66,0.25)' }}>
        <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Objetivo desta página:</strong> controlar os parâmetros globais do módulo de astrólogos profissionais.
          Aqui você define se o módulo está ativo, o número máximo de clientes que cada astrólogo pode cadastrar,
          o limite de análises por mês por astrólogo e o bucket de armazenamento de documentos gerados.
          Alterações entram em vigor imediatamente para todos os astrólogos cadastrados.
        </p>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label className="form-label">Módulo habilitado (true/false)</label>
            <input
              className="form-input"
              value={settings['astrologer_module_enabled'] ?? 'true'}
              onChange={e => setSettings(s => ({ ...s, astrologer_module_enabled: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Máx. clientes por astrólogo</label>
            <input
              className="form-input"
              type="number"
              min={1}
              value={settings['astrologer_max_clients_per_astrologer'] ?? '500'}
              onChange={e => setSettings(s => ({ ...s, astrologer_max_clients_per_astrologer: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Máx. análises por mês (por astrólogo)</label>
            <input
              className="form-input"
              type="number"
              min={1}
              value={settings['astrologer_max_analyses_per_month'] ?? '200'}
              onChange={e => setSettings(s => ({ ...s, astrologer_max_analyses_per_month: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Storage bucket (documentos)</label>
            <input
              className="form-input"
              value={settings['astrologer_doc_storage_bucket'] ?? 'astrologer-docs'}
              onChange={e => setSettings(s => ({ ...s, astrologer_doc_storage_bucket: e.target.value }))}
            />
          </div>
        </div>

        <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Salvando…' : 'Salvar configurações'}
          </button>
          {saved && <span style={{ fontSize: 'var(--text-caption)', color: '#4ade80' }}>✓ Salvo</span>}
        </div>
      </div>
    </div>
  );
}
